import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import Modal from '../../comun/Modal';

const EMPTY_PROMO = {
    name: '',
    description: '',
    price: '',
    image: null,
    promotion_category: '',
    is_standalone: false,
    combo_products: [{ product_id: '', quantity: 1 }],
    is_active: true
};

const EMPTY_CATEGORY = { name: '', color: '#c9973a', is_active: true };

const slugify = (text) => text.toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/ /g, '-')
    .replace(/[^\w-]+/g, '');

const Promociones = () => {
    const [promotions, setPromotions] = useState([]);
    const [products, setProducts] = useState([]);
    const [promoCategories, setPromoCategories] = useState([]);
    const [selectedFilter, setSelectedFilter] = useState('all');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);

    const [newPromo, setNewPromo] = useState(EMPTY_PROMO);
    const [editingPromo, setEditingPromo] = useState(null);

    const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
    const [categoryForm, setCategoryForm] = useState(EMPTY_CATEGORY);
    const [editingCategory, setEditingCategory] = useState(null);

    const fetchPromotions = async () => {
        try {
            const response = await api.get('/api/menu/combos/?is_promotion=true', {
                baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
            });
            setPromotions(response.data.results || response.data || []);
        } catch (err) {
            console.error('Error fetching promotions:', err);
            setError('Error al cargar las promociones');
        } finally {
            setLoading(false);
        }
    };

    const fetchProducts = async () => {
        try {
            const response = await api.get('/api/menu/products/', {
                baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
            });
            setProducts(response.data.results || response.data || []);
        } catch (err) {
            console.error('Error fetching products:', err);
        }
    };

    const fetchPromoCategories = async () => {
        try {
            const response = await api.get('/api/menu/promotion-categories/', {
                baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
            });
            setPromoCategories(response.data.results || response.data || []);
        } catch (err) {
            console.error('Error fetching promotion categories:', err);
        }
    };

    useEffect(() => {
        fetchPromotions();
        fetchProducts();
        fetchPromoCategories();
    }, []);

    const filteredPromotions = promotions.filter(promo => {
        if (selectedFilter === 'all') return true;
        if (selectedFilter === 'none') return !promo.promotion_category;
        return promo.promotion_category === selectedFilter;
    });

    // =====================================
    // CATEGORÍAS DE PROMOCIONES
    // =====================================
    const resetCategoryForm = () => {
        setEditingCategory(null);
        setCategoryForm(EMPTY_CATEGORY);
    };

    const handleEditCategory = (cat) => {
        setEditingCategory(cat);
        setCategoryForm({ name: cat.name, color: cat.color || '#c9973a', is_active: cat.is_active });
    };

    const handleCategorySubmit = async (e) => {
        e.preventDefault();
        const payload = { ...categoryForm, slug: slugify(categoryForm.name) };
        try {
            if (editingCategory) {
                await api.patch(`/api/menu/promotion-categories/${editingCategory.id}/`, payload, {
                    baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
                });
            } else {
                await api.post('/api/menu/promotion-categories/', {
                    ...payload,
                    display_order: promoCategories.length + 1
                }, {
                    baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
                });
            }
            resetCategoryForm();
            fetchPromoCategories();
        } catch (err) {
            console.error('Error saving promotion category:', err);
            alert('Error al guardar la categoría. Verifique que el nombre no esté repetido.');
        }
    };

    const handleDeleteCategory = async (cat) => {
        const msg = cat.promotions_count > 0
            ? `La categoría "${cat.name}" tiene ${cat.promotions_count} promoción(es). Quedarán sin categoría. ¿Eliminar?`
            : `¿Eliminar la categoría "${cat.name}"?`;
        if (!window.confirm(msg)) return;
        try {
            await api.delete(`/api/menu/promotion-categories/${cat.id}/`, {
                baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
            });
            if (selectedFilter === cat.id) setSelectedFilter('all');
            if (editingCategory && editingCategory.id === cat.id) resetCategoryForm();
            fetchPromoCategories();
            fetchPromotions();
        } catch (err) {
            console.error('Error deleting promotion category:', err);
            alert('Error al eliminar la categoría');
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;
        setNewPromo(prev => ({ ...prev, [name]: newValue }));
    };

    const handleAddProduct = () => {
        setNewPromo(prev => ({
            ...prev,
            combo_products: [...prev.combo_products, { product_id: '', quantity: 1 }]
        }));
    };

    const handleRemoveProduct = (index) => {
        setNewPromo(prev => {
            const newProducts = [...prev.combo_products];
            newProducts.splice(index, 1);
            return { ...prev, combo_products: newProducts };
        });
    };

    const handleComboProductChange = (index, field, value) => {
        setNewPromo(prev => {
            const newProducts = [...prev.combo_products];
            newProducts[index] = { ...newProducts[index], [field]: value };
            return { ...prev, combo_products: newProducts };
        });
    };

    const handleImageChange = (e) => {
        setNewPromo(prev => ({ ...prev, image: e.target.files[0] }));
    };

    const handleEditPromo = async (promo) => {
        try {
            // Obtener los productos del combo para rellenar el formulario
            const response = await api.get(`/api/menu/combos/${promo.id}/products/`, {
                baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
            });
            const promoProducts = response.data;
            
            setEditingPromo(promo);
            setNewPromo({
                name: promo.name,
                description: promo.description,
                price: promo.price,
                is_active: promo.is_active,
                image: null,
                promotion_category: promo.promotion_category || '',
                is_standalone: promoProducts.length === 0,
                combo_products: promoProducts.length > 0 ?
                    promoProducts.map(p => ({ product_id: p.product.id, quantity: p.quantity })) : 
                    [{ product_id: '', quantity: 1 }]
            });
            setIsModalOpen(true);
        } catch (err) {
            console.error('Error fetching promo products:', err);
        }
    };

    const handleDeletePromo = async (id) => {
        if (window.confirm('¿Estás seguro de eliminar esta promoción?')) {
            try {
                await api.delete(`/api/menu/combos/${id}/`, {
                    baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
                });
                fetchPromotions();
            } catch (err) {
                console.error('Error deleting promo:', err);
                alert('Error al eliminar la promoción');
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!newPromo.is_standalone && !newPromo.combo_products.some(p => p.product_id)) {
            alert("Por favor seleccione al menos un producto para la promoción, o márquela como promoción independiente.");
            return;
        }

        const slug = slugify(newPromo.name);

        // Paso 1: Guardar la promoción (Combo) usando FormData (para imagen)
        const formData = new FormData();
        formData.append('name', newPromo.name);
        formData.append('slug', slug);
        formData.append('description', newPromo.description);
        formData.append('price', newPromo.price);
        formData.append('is_promotion', 'true');
        formData.append('is_active', newPromo.is_active ? 'true' : 'false');
        
        if (newPromo.image instanceof File) {
            formData.append('image', newPromo.image);
        }

        try {
            let comboId;
            if (editingPromo) {
                await api.patch(`/api/menu/combos/${editingPromo.id}/`, formData, {
                    baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE,
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                comboId = editingPromo.id;
            } else {
                const res = await api.post('/api/menu/combos/', formData, {
                    baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE,
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                comboId = res.data.id;
            }
            
            // Paso 2: Actualizar categoría y productos de la promoción (Json).
            // Una promoción independiente se envía sin productos.
            await api.patch(`/api/menu/combos/${comboId}/`, {
                promotion_category: newPromo.promotion_category || null,
                combo_products: newPromo.is_standalone ? [] : newPromo.combo_products
                    .filter(p => p.product_id)
                    .map((p, index) => ({
                        product_id: p.product_id,
                        quantity: parseInt(p.quantity, 10),
                        display_order: index + 1
                    }))
            }, {
                baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE,
            });

            setIsModalOpen(false);
            setNewPromo(EMPTY_PROMO);
            setEditingPromo(null);
            fetchPromotions();
        } catch (err) {
            console.error('Error saving promotion:', err);
            alert('Error al guardar la promoción. Verifique los datos.');
        }
    };

    if (loading) return <div>Cargando promociones...</div>;
    if (error) return <div className="alert alert-error">{error}</div>;

    return (
        <div className="page-container">
            <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                    <h2 style={{ margin: 0, color: '#2b211b' }}>Gestión de Promociones</h2>
                    <p style={{ margin: 0, color: '#8a7d70', fontSize: '0.9rem' }}>Crea ofertas, descuentos, 2x1 o sorteos, con o sin productos asociados.</p>
                </div>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <button className="btn btn-secondary" onClick={() => {
                        resetCategoryForm();
                        setIsCategoryModalOpen(true);
                    }}>
                        Gestionar categorías
                    </button>
                    <button className="btn btn-primary" onClick={() => {
                        setEditingPromo(null);
                        setNewPromo({
                            ...EMPTY_PROMO,
                            promotion_category: selectedFilter !== 'all' && selectedFilter !== 'none' ? selectedFilter : ''
                        });
                        setIsModalOpen(true);
                    }} style={{ backgroundColor: '#c9973a', borderColor: '#c9973a', color: '#000', fontWeight: 'bold' }}>
                        + Nueva Promoción
                    </button>
                </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '15px' }}>
                {[
                    { id: 'all', name: 'Todas', count: promotions.length },
                    ...promoCategories.map(cat => ({
                        id: cat.id,
                        name: cat.name,
                        color: cat.color,
                        inactive: !cat.is_active,
                        count: promotions.filter(p => p.promotion_category === cat.id).length
                    })),
                    { id: 'none', name: 'Sin categoría', count: promotions.filter(p => !p.promotion_category).length }
                ].map(chip => {
                    const isSelected = selectedFilter === chip.id;
                    const accent = chip.color || '#c9973a';
                    return (
                        <button
                            key={chip.id}
                            type="button"
                            onClick={() => setSelectedFilter(chip.id)}
                            style={{
                                padding: '0.4rem 0.9rem',
                                borderRadius: '999px',
                                border: `2px solid ${accent}`,
                                backgroundColor: isSelected ? accent : '#fff',
                                color: isSelected ? '#fff' : '#4a3f36',
                                fontWeight: 600,
                                fontSize: '0.85rem',
                                cursor: 'pointer',
                                opacity: chip.inactive ? 0.6 : 1
                            }}
                        >
                            {chip.name} ({chip.count})
                        </button>
                    );
                })}
            </div>

            <div className="table-responsive">
                <table className="table">
                    <thead>
                        <tr>
                            <th>Imagen</th>
                            <th>Nombre Promoción</th>
                            <th>Categoría</th>
                            <th>Tipo</th>
                            <th>Precio Cobrado</th>
                            <th>Estado</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredPromotions.length === 0 ? (
                            <tr><td colSpan="7" style={{textAlign: 'center', padding: '2rem'}}>
                                {promotions.length === 0
                                    ? 'No hay promociones registradas. ¡Crea una para atraer clientes!'
                                    : 'No hay promociones en esta categoría.'}
                            </td></tr>
                        ) : (
                            filteredPromotions.map(promo => (
                                <tr key={promo.id}>
                                    <td>
                                        {promo.image ? (
                                            <img
                                                src={promo.image.startsWith('http') ? promo.image : `${process.env.REACT_APP_FAST_FOOD_SERVICE}${promo.image}`}
                                                alt={promo.name}
                                                style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '8px' }}
                                            />
                                        ) : (
                                            <div style={{ width: '50px', height: '50px', backgroundColor: '#e6dfd3', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#b5a898', fontSize: '10px' }}>Sin foto</div>
                                        )}
                                    </td>
                                    <td>
                                        <div style={{fontWeight: 'bold', color: '#2b211b'}}>{promo.name}</div>
                                        <div style={{fontSize: '0.8rem', color: '#8a7d70'}}>{promo.description}</div>
                                    </td>
                                    <td>
                                        {promo.promotion_category_name || <span style={{color: '#b5a898'}}>Sin categoría</span>}
                                    </td>
                                    <td>
                                        {promo.products_count > 0
                                            ? `${promo.products_count} producto(s)`
                                            : <span style={{backgroundColor: '#efe8dc', color: '#6b5e51', padding: '0.2rem 0.5rem', borderRadius: '12px', fontSize: '0.8rem'}}>Independiente</span>
                                        }
                                    </td>
                                    <td>
                                        <span style={{fontWeight: 'bold', color: '#5f8a5e'}}>${promo.price}</span>
                                    </td>
                                    <td>
                                        {promo.is_active ? 
                                            <span style={{backgroundColor: '#d4e4ca', color: '#3d5c37', padding: '0.2rem 0.5rem', borderRadius: '12px', fontSize: '0.8rem'}}>Activa</span> : 
                                            <span style={{backgroundColor: '#f0cfc5', color: '#6b2f22', padding: '0.2rem 0.5rem', borderRadius: '12px', fontSize: '0.8rem'}}>Inactiva</span>
                                        }
                                    </td>
                                    <td>
                                        <button
                                            className="btn btn-secondary"
                                            onClick={() => handleEditPromo(promo)}
                                            style={{ marginRight: '5px', padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
                                        >
                                            Editar
                                        </button>
                                        <button
                                            className="btn btn-danger"
                                            onClick={() => handleDeletePromo(promo.id)}
                                            style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
                                        >
                                            Eliminar
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingPromo ? "Editar Promoción" : "Nueva Promoción"}>
                <form onSubmit={handleSubmit}>
                    <div style={{backgroundColor: '#faf7f1', padding: '15px', borderRadius: '8px', marginBottom: '15px', border: '1px solid #efe8dc'}}>
                        <h4 style={{marginTop: 0, marginBottom: '15px', fontSize: '1rem', color: '#6b5e51'}}>1. Definir la Oferta</h4>
                        <div className="form-group">
                            <label>Nombre de la Promoción (Ej. 2x1 Fresas con Crema)</label>
                            <input type="text" name="name" value={newPromo.name} onChange={handleInputChange} required />
                        </div>
                        <div className="form-group">
                            <label>Descripción corta</label>
                            <textarea name="description" value={newPromo.description} onChange={handleInputChange} rows="2" />
                        </div>
                        <div className="form-group">
                            <label>Categoría de la promoción</label>
                            <select name="promotion_category" value={newPromo.promotion_category} onChange={handleInputChange}>
                                <option value="">Sin categoría</option>
                                {promoCategories.map(cat => (
                                    <option key={cat.id} value={cat.id}>{cat.name}{cat.is_active ? '' : ' (inactiva)'}</option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Imagen Promocional</label>
                            <input type="file" accept="image/*" onChange={handleImageChange} required={!editingPromo} />
                        </div>
                    </div>

                    <div style={{backgroundColor: '#f7f1e7', padding: '15px', borderRadius: '8px', marginBottom: '15px', border: '1px solid #e8d9c0'}}>
                        <h4 style={{marginTop: 0, marginBottom: '15px', fontSize: '1rem', color: '#3a2618'}}>2. Reglas de Descuento e Inventario</h4>
                        <div className="form-group">
                            <label style={{ fontWeight: 'normal' }}>
                                <input type="checkbox" name="is_standalone" checked={newPromo.is_standalone} onChange={handleInputChange} style={{ marginRight: '8px' }} />
                                Promoción independiente (no ligada a productos del menú)
                            </label>
                            {newPromo.is_standalone && (
                                <small style={{color: '#8a7d70', display: 'block', marginTop: '5px'}}>
                                    Se vende con su propio nombre y precio. No descuenta inventario de ningún producto.
                                </small>
                            )}
                        </div>
                        {!newPromo.is_standalone && (<>
                        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '10px' }}>¿Qué productos incluye la promoción?</label>
                        {newPromo.combo_products.map((cp, index) => (
                            <div key={index} style={{ backgroundColor: '#fff', padding: '15px', borderRadius: '8px', border: '1px solid #e6dfd3', marginBottom: '10px' }}>
                                <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-start' }}>
                                    <div className="form-group" style={{ flex: 2, marginBottom: '0' }}>
                                        <label style={{ fontSize: '0.9rem' }}>Producto</label>
                                        <select 
                                            className="form-control" 
                                            value={cp.product_id} 
                                            onChange={(e) => handleComboProductChange(index, 'product_id', e.target.value)} 
                                            required
                                        >
                                            <option value="">Seleccione un producto</option>
                                            {products.map(prod => (
                                                <option key={prod.id} value={prod.id}>{prod.name} (Stock: {prod.is_available ? 'Disp.' : 'Agotado'})</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group" style={{ flex: 1, marginBottom: '0' }}>
                                        <label style={{ fontSize: '0.9rem' }}>Cantidad a entregar</label>
                                        <input 
                                            type="number" 
                                            value={cp.quantity} 
                                            onChange={(e) => handleComboProductChange(index, 'quantity', e.target.value)} 
                                            min="1" 
                                            step="1" 
                                            required 
                                        />
                                    </div>
                                    {newPromo.combo_products.length > 1 && (
                                        <div style={{ alignSelf: 'flex-end', paddingBottom: '0.5rem' }}>
                                            <button type="button" onClick={() => handleRemoveProduct(index)} style={{ backgroundColor: 'transparent', border: 'none', color: '#b04a3d', cursor: 'pointer', fontWeight: 'bold' }}>
                                                X Quitar
                                            </button>
                                        </div>
                                    )}
                                </div>
                                {cp.product_id && products.find(p => p.id === cp.product_id) && (() => {
                                    const sp = products.find(p => p.id === cp.product_id);
                                    return (
                                        <div style={{display: 'flex', alignItems: 'center', marginTop: '10px', padding: '10px', backgroundColor: '#faf7f1', borderRadius: '8px'}}>
                                            {sp.image ? (
                                                <img src={sp.image.startsWith('http') ? sp.image : `${process.env.REACT_APP_FAST_FOOD_SERVICE}${sp.image}`} alt={sp.name} style={{width: '40px', height: '40px', objectFit: 'contain', borderRadius: '4px', marginRight: '10px'}} />
                                            ) : (
                                                <div style={{width: '40px', height: '40px', backgroundColor: '#e6dfd3', borderRadius: '4px', marginRight: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', color: '#a89c8c', textAlign: 'center'}}>Sin foto</div>
                                            )}
                                            <div>
                                                <div style={{fontWeight: 'bold', color: '#2b211b', fontSize: '0.9rem'}}>{sp.name}</div>
                                                <div style={{color: '#8a7d70', fontSize: '0.8rem'}}>Precio Base: <span style={{fontWeight: 'bold', color: '#5f8a5e'}}>${sp.price}</span></div>
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>
                        ))}
                        
                        <button 
                            type="button" 
                            onClick={handleAddProduct} 
                            style={{ backgroundColor: '#e6dfd3', color: '#6b5e51', border: 'none', padding: '8px 12px', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', marginBottom: '15px', fontSize: '0.9rem' }}
                        >
                            + Agregar otro producto a la promoción
                        </button>
                        </>)}

                        <div className="form-group" style={{ borderTop: '1px solid #e8d9c0', paddingTop: '15px' }}>
                            <label style={{ color: '#3a2618', fontWeight: 'bold' }}>Precio Final a Cobrar ($)</label>
                            <input type="number" name="price" value={newPromo.price} onChange={handleInputChange} min="0" step="0.01" required style={{ maxWidth: '200px', display: 'block' }} />
                            <small style={{color: '#8a7d70', display: 'block', marginTop: '5px'}}>Ej: Para 2x1 pon el precio de 1. Si es un combo de varios productos, pon el precio total del combo.</small>
                        </div>
                    </div>

                    <div className="form-group">
                        <label>
                            <input type="checkbox" name="is_active" checked={newPromo.is_active} onChange={handleInputChange} style={{ marginRight: '8px' }} />
                            Promoción Activa (Visible en el Punto de Venta)
                        </label>
                    </div>

                    <div className="form-actions">
                        <button type="button" className="btn btn-secondary" onClick={() => setIsModalOpen(false)}>Cancelar</button>
                        <button type="submit" className="btn btn-primary" style={{backgroundColor: '#c9973a', borderColor: '#c9973a', color: '#000', fontWeight: 'bold'}}>Guardar Promoción</button>
                    </div>
                </form>
            </Modal>

            <Modal isOpen={isCategoryModalOpen} onClose={() => setIsCategoryModalOpen(false)} title="Categorías de Promociones">
                <p style={{ marginTop: 0, color: '#8a7d70', fontSize: '0.9rem' }}>
                    Estas categorías solo organizan las promociones (ej. Combos, 2x1, Temporada). No afectan las categorías de productos.
                </p>

                <form onSubmit={handleCategorySubmit} style={{ backgroundColor: '#faf7f1', padding: '15px', borderRadius: '8px', marginBottom: '15px', border: '1px solid #efe8dc' }}>
                    <h4 style={{ marginTop: 0, marginBottom: '15px', fontSize: '1rem', color: '#6b5e51' }}>
                        {editingCategory ? `Editar "${editingCategory.name}"` : 'Nueva categoría'}
                    </h4>
                    <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                        <div className="form-group" style={{ flex: 2, minWidth: '180px', marginBottom: 0 }}>
                            <label>Nombre</label>
                            <input
                                type="text"
                                value={categoryForm.name}
                                onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="Ej. 2x1, Combos familiares"
                                required
                            />
                        </div>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                            <label>Color</label>
                            <input
                                type="color"
                                value={categoryForm.color}
                                onChange={(e) => setCategoryForm(prev => ({ ...prev, color: e.target.value }))}
                                style={{ width: '50px', height: '38px', padding: '2px', border: '1px solid #d6ccbc', borderRadius: '0.375rem' }}
                            />
                        </div>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                            <label style={{ fontWeight: 'normal' }}>
                                <input
                                    type="checkbox"
                                    checked={categoryForm.is_active}
                                    onChange={(e) => setCategoryForm(prev => ({ ...prev, is_active: e.target.checked }))}
                                    style={{ marginRight: '8px' }}
                                />
                                Activa
                            </label>
                        </div>
                    </div>
                    <div className="form-actions" style={{ marginTop: '15px' }}>
                        {editingCategory && (
                            <button type="button" className="btn btn-secondary" onClick={resetCategoryForm}>Cancelar edición</button>
                        )}
                        <button type="submit" className="btn btn-primary" style={{ backgroundColor: '#c9973a', borderColor: '#c9973a', color: '#000', fontWeight: 'bold' }}>
                            {editingCategory ? 'Guardar cambios' : '+ Agregar categoría'}
                        </button>
                    </div>
                </form>

                {promoCategories.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '1rem', color: '#8a7d70' }}>Aún no hay categorías de promociones.</div>
                ) : (
                    <div className="table-responsive">
                        <table className="table" style={{ minWidth: 0 }}>
                            <thead>
                                <tr>
                                    <th>Categoría</th>
                                    <th>Promociones</th>
                                    <th>Estado</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {promoCategories.map(cat => (
                                    <tr key={cat.id}>
                                        <td>
                                            <span style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '50%', backgroundColor: cat.color || '#c9973a', marginRight: '8px', verticalAlign: 'middle' }} />
                                            <span style={{ fontWeight: 'bold', color: '#2b211b' }}>{cat.name}</span>
                                        </td>
                                        <td>{cat.promotions_count}</td>
                                        <td>{cat.is_active ? 'Activa' : 'Inactiva'}</td>
                                        <td>
                                            <button
                                                type="button"
                                                className="btn btn-secondary"
                                                onClick={() => handleEditCategory(cat)}
                                                style={{ marginRight: '5px', padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
                                            >
                                                Editar
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-danger"
                                                onClick={() => handleDeleteCategory(cat)}
                                                style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
                                            >
                                                Eliminar
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default Promociones;

// Mismos estilos que en Inventario.js
const styles = `
    .page-container { padding: 20px; max-width: 1200px; margin: 0 auto; }
    .table-responsive { width: 100%; overflow-x: auto; border-radius: 0.5rem; box-shadow: 0 1px 3px rgba(0,0,0,0.1); border: 1px solid #e6dfd3; }
    .table { width: 100%; border-collapse: collapse; background-color: white; min-width: 600px; }
    .table th, .table td { padding: 0.75rem 1rem; text-align: left; border-bottom: 1px solid #e6dfd3; }
    .table th { background-color: var(--sidebar-bg); font-weight: 600; color: #4a3f36; font-size: 0.875rem; text-transform: uppercase; }
    .table td { font-size: 0.875rem; color: #6b5e51; }
    .form-group { margin-bottom: 1rem; }
    .form-group label { display: block; margin-bottom: 0.5rem; font-weight: 500; color: #4a3f36; }
    .form-control, input[type="text"], input[type="number"], textarea, select { width: 100%; padding: 0.5rem; border: 1px solid #d6ccbc; border-radius: 0.375rem; font-size: 1rem; }
    .form-actions { display: flex; justify-content: flex-end; gap: 1rem; margin-top: 1.5rem; }
`;

const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);
